import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';

const CHECKPOINT_LABELS = {
  deal_view: 'Deal page',
  cart: 'Cart',
  checkout: 'Checkout'
};

/**
 * Section 11 — return policy shown up to 3 times before purchase (deal view, cart, checkout).
 */
export default function DealReturnPolicyDisclosure({
  dealId,
  checkpoint = 'cart',
  onAcceptedChange,
  requireAcceptance = false,
  className = ''
}) {
  const [loading, setLoading] = useState(!!dealId);
  const [error, setError] = useState('');
  const [policy, setPolicy] = useState(null);
  const [accepted, setAccepted] = useState(false);

  const storageKey = dealId ? `deal_return_ack_${dealId}_${checkpoint}` : null;

  useEffect(() => {
    if (!dealId) {
      setLoading(false);
      setPolicy(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/public/deals/${dealId}/return-policy`, {
          params: { checkpoint }
        });
        const data = res?.data?.data || res?.data;
        if (!cancelled) {
          setPolicy(data?.policy || null);
          const prior = storageKey && sessionStorage.getItem(storageKey) === '1';
          setAccepted(!!prior);
          if (prior && onAcceptedChange) onAcceptedChange(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || 'Could not load return policy.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId, checkpoint, storageKey, onAcceptedChange]);

  const toggleAccepted = useCallback(
    (next) => {
      setAccepted(next);
      if (storageKey) {
        if (next) sessionStorage.setItem(storageKey, '1');
        else sessionStorage.removeItem(storageKey);
      }
      if (onAcceptedChange) onAcceptedChange(next);
    },
    [storageKey, onAcceptedChange]
  );

  if (!dealId) return null;
  if (loading) {
    return (
      <div className={`text-sm text-gray-500 ${className}`}>Loading return policy…</div>
    );
  }
  if (error) {
    return (
      <div className={`text-sm text-red-600 ${className}`} role="alert">
        {error}
      </div>
    );
  }
  if (!policy) return null;

  const isFinalSale = policy.finalSale || !policy.allowReturns;
  const checkpointLabel = CHECKPOINT_LABELS[checkpoint] || checkpoint;

  return (
    <div
      className={`rounded-lg border p-3 text-sm ${
        isFinalSale ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'
      } ${className}`}
    >
      <p className="font-semibold text-gray-900">
        Return policy ({checkpointLabel} — disclosure required before purchase)
      </p>
      <p className="mt-1 text-gray-700">{policy.policySummary}</p>
      {policy.allOrNothing ? (
        <p className="mt-1 text-amber-800 font-medium">All combo items must be returned together.</p>
      ) : null}
      {requireAcceptance ? (
        <label className="mt-3 flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={accepted}
            onChange={(e) => toggleAccepted(e.target.checked)}
          />
          <span>
            I have read and accept this return policy. This deal&apos;s policy is locked and cannot be
            changed.
          </span>
        </label>
      ) : null}
    </div>
  );
}

export function allDealReturnPoliciesAccepted(dealIds, checkpoint = 'checkout') {
  if (!dealIds?.length) return true;
  return dealIds.every((id) => sessionStorage.getItem(`deal_return_ack_${id}_${checkpoint}`) === '1');
}
